import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

/**
 * Hands off to /api/stripe/portal which mints a Stripe Customer Portal
 * session for the current user. On return, the user lands back on their
 * profile (per portal.ts return_url).
 *
 * Note on the "just cancelled" case: a user who cancels in the Stripe
 * Portal then navigates back to a paywalled page may continue to see
 * content for up to 1 hour (their JWT still has the old has_active_access
 * claim). Acceptable — they paid for the period; downgrade lands at the
 * next JWT refresh.
 */

export default function BillingPortal() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        const { data: sess } = await supabase.auth.getSession();
        const token = sess.session?.access_token;
        if (!token) { navigate('/sign-in', { replace: true }); return; }
        const res = await fetch('/api/stripe/portal', {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({}),
          signal: ctrl.signal,
        });
        const data = await res.json().catch(() => ({}));
        if (ctrl.signal.aborted) return;
        if (!res.ok || !data?.url) {
          setError(data?.error ?? 'Stripe portal is not configured yet.');
          return;
        }
        window.location.assign(data.url);
      } catch (err: any) {
        if (ctrl.signal.aborted || err?.name === 'AbortError') return;
        setError(err?.message ?? 'Stripe portal failed.');
      }
    })();
    return () => ctrl.abort();
  }, [navigate]);

  return (
    <div
      className="container container--sm"
      style={{ padding: 'var(--space-24) var(--gutter)', textAlign: 'center' }}
    >
      <p className="kicker" style={{ marginBottom: 'var(--space-4)' }}>Billing</p>
      {error ? (
        <>
          <h1 style={{ fontFamily: 'var(--f-display)', fontWeight: 400, fontSize: 'var(--t-2xl)', marginBottom: 'var(--space-4)' }}>
            Stripe portal isn't live yet.
          </h1>
          <p style={{ fontFamily: 'var(--f-body)', color: 'var(--text-soft)' }}>{error}</p>
        </>
      ) : (
        <p style={{ fontFamily: 'var(--f-body)', color: 'var(--text-soft)' }}>
          Opening Stripe portal…
        </p>
      )}
    </div>
  );
}
